import { datadogRum } from "@datadog/browser-rum";
import type {
  DatadogRumService,
  DatadogRumConfig,
  RumUser,
  RumActionContextType,
} from "./datadog-rum.interface";
import { MonitoringErrorMessages } from "../constants/monitoring-errors.constants";

type InitializationStateType =
  | "not-initialized"
  | "disabled"
  | "failed"
  | "initialized";

export class DatadogRumServiceImpl implements DatadogRumService {
  private static instance: DatadogRumServiceImpl | undefined;
  private initState: InitializationStateType = "not-initialized";
  private initError?: Error;

  private constructor() {}

  public static getInstance(): DatadogRumServiceImpl {
    DatadogRumServiceImpl.instance ??= new DatadogRumServiceImpl();
    return DatadogRumServiceImpl.instance;
  }

  private handleUninitializedCall(methodName: string): void {
    const reason =
      this.initState === "disabled"
        ? "RUM is disabled via configuration"
        : this.initState === "failed"
          ? `RUM initialization failed: ${this.initError?.message ?? "unknown error"}`
          : "RUM not initialized";

    if (import.meta.env.DEV) {
      console.debug(`Datadog RUM ${methodName}() called but ${reason}.`);
    } else {
      console.warn(
        JSON.stringify({
          level: "warn",
          message: MonitoringErrorMessages.RUM_METHOD_CALLED_UNINITIALIZED(
            methodName,
            reason,
          ),
          context: { methodName, initState: this.initState },
        }),
      );
    }
  }

  public initialize(config: DatadogRumConfig): void {
    // Prevent double initialization
    if (this.initState !== "not-initialized") {
      console.warn(
        JSON.stringify({
          level: "warn",
          message: MonitoringErrorMessages.RUM_ALREADY_INITIALIZED,
          context: { initState: this.initState },
        }),
      );
      return;
    }

    /* eslint-disable @typescript-eslint/no-unsafe-assignment -- Vite env vars are typed as any */

    // This fail-safe default prevents accidental monitoring activation and associated costs.
    const ddEnabled: string | undefined = import.meta.env["VITE_DD_ENABLED"];
    if (ddEnabled !== "true") {
      console.log(
        JSON.stringify({
          level: "info",
          message: MonitoringErrorMessages.RUM_DISABLED_VIA_FLAG,
        }),
      );
      this.initState = "disabled";
      return;
    }

    const applicationId: string | undefined =
      config.applicationId ?? import.meta.env["VITE_DD_APPLICATION_ID"];
    const clientToken: string | undefined =
      config.clientToken ?? import.meta.env["VITE_DD_CLIENT_TOKEN"];
    const environment: string =
      import.meta.env["VITE_ENVIRONMENT"] ?? "development";
    const version: string = import.meta.env["VITE_APP_VERSION"] ?? "unknown";
    /* eslint-enable @typescript-eslint/no-unsafe-assignment */

    if (
      applicationId === undefined ||
      applicationId === "" ||
      clientToken === undefined ||
      clientToken === ""
    ) {
      console.warn(
        JSON.stringify({
          level: "warn",
          message: MonitoringErrorMessages.RUM_CREDENTIALS_NOT_CONFIGURED,
        }),
      );
      this.initState = "disabled";
      return;
    }

    try {
      datadogRum.init({
        applicationId,
        clientToken,
        site: "datadoghq.com",
        service: config.service,
        env: environment,
        version,

        sessionSampleRate: 100,

        sessionReplaySampleRate: environment === "production" ? 20 : 100,

        trackUserInteractions: true,

        trackResources: true,

        trackLongTasks: true,

        defaultPrivacyLevel: "mask-user-input",
      });

      datadogRum.startSessionReplayRecording();

      this.initState = "initialized";
      console.log(
        JSON.stringify({
          level: "info",
          message: MonitoringErrorMessages.RUM_INIT_SUCCESS(
            config.service,
            environment,
          ),
        }),
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.initState = "failed";
      this.initError =
        error instanceof Error ? error : new Error(String(error));

      const errorDetails = {
        error: errorMessage,
        stack: error instanceof Error ? error.stack : undefined,
        service: config.service,
        environment,
        applicationId,
        clientTokenPresent: true,
      };

      console.error(
        JSON.stringify({
          level: "error",
          message: MonitoringErrorMessages.RUM_INIT_CRITICAL_FAILURE(
            config.service,
          ),
          context: errorDetails,
        }),
      );

      // Do NOT throw - monitoring failures should never prevent application startup
      // The application should function normally even if RUM initialization fails
      // The initState is set to "failed" so subsequent RUM calls will be no-ops
    }
  }

  public setUser(user: RumUser): void {
    if (this.initState !== "initialized") {
      this.handleUninitializedCall("setUser");
      return;
    }
    try {
      datadogRum.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      console.error(
        JSON.stringify({
          level: "error",
          message: MonitoringErrorMessages.RUM_SET_USER_FAILED(
            user.id,
            errorMessage,
          ),
          context: {
            error: errorMessage,
            stack: error instanceof Error ? error.stack : undefined,
            userId: user.id,
            hasName: user.name !== undefined,
            hasEmail: user.email !== undefined,
          },
        }),
      );

      // Do NOT throw - monitoring operations should not break user workflows
      // The error is logged for debugging, but the application continues normally
    }
  }

  public clearUser(): void {
    if (this.initState !== "initialized") {
      this.handleUninitializedCall("clearUser");
      return;
    }
    try {
      datadogRum.clearUser();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      console.error(
        JSON.stringify({
          level: "error",
          message: MonitoringErrorMessages.RUM_CLEAR_USER_FAILED(errorMessage),
          context: {
            error: errorMessage,
            stack: error instanceof Error ? error.stack : undefined,
          },
        }),
      );

      // Do NOT throw - monitoring operations should not break user workflows
      // The error is logged for debugging, but the application continues normally
    }
  }

  public addAction(name: string, context?: RumActionContextType): void {
    if (this.initState !== "initialized") {
      this.handleUninitializedCall("addAction");
      return;
    }
    try {
      datadogRum.addAction(name, context);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      console.error(
        JSON.stringify({
          level: "error",
          message: MonitoringErrorMessages.RUM_ADD_ACTION_FAILED(
            name,
            errorMessage,
          ),
          context: {
            error: errorMessage,
            stack: error instanceof Error ? error.stack : undefined,
            actionName: name,
            hasContext: context !== undefined,
          },
        }),
      );

      // Do NOT throw - monitoring operations should not break user workflows
      // The error is logged for debugging, but the application continues normally
    }
  }
}
